import { InterfaceDesignProvider } from "src/shared/ui/src/design/InterfaceDesign";
import AthleteEntryPage from "./pages/athlete-entry/AthleteEntryPage";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { lazy, Suspense } from "react";
import HomePage from "./pages/home/HomePage";
import { BrowserRouter, Outlet, Route, Routes } from "react-router-dom";
import { AnalyticsBanner } from "./components/AnalyticsBanner";
import MainLayout from "./components/layout/MainLayout";
import MinimalLayout from "./components/layout/MinimalLayout";
import RouteLoadingFallback from "./components/RouteLoadingFallback.tsx";
import {
  LicensedTermsRouteGuard,
  LicenseTermsAcceptanceProvider,
} from "./contexts/LicenseTermsAcceptanceContext";
import LayoutDisplayPage from "./pages/display/LayoutDisplayPage";
import LayoutDisplayPreviewPage from "./pages/display/LayoutDisplayPreviewPage";
import { WidgetView } from "./pages/widget/WidgetView";
import { AnalyticsProvider } from "./contexts/AnalyticsContext";
import { ConfirmDialogProvider } from "./shared/ui/src/contexts/ConfirmDialogContext";
import { LanguageProvider } from "./shared/ui/src/contexts/LanguageContext";
import { SnackProvider } from "./shared/ui/src/contexts/SnackContext";

// Route-based code splitting: Lazy load all page components
// This dramatically reduces the initial bundle size by only loading
// the code for the page the user is visiting
const AdminGlobalPage = lazy(
  () => import("./pages/admin/pages/AdminGlobalPage"),
);
const AnalyticsPage = lazy(() => import("./pages/admin/pages/AnalyticsPage"));
const SignIn = lazy(() => import("./pages/admin/pages/SignIn"));
const ContactPage = lazy(() => import("./pages/contact/ContactPage"));
const StreamViewPage = lazy(
  () => import("./pages/group/stream-view/StreamViewPage"),
);
const SpeakerViewPage = lazy(
  () => import("./pages/group/speaker-view/SpeakerViewPage"),
);
const SpeakerLandingPage = lazy(
  () => import("./pages/speaker/SpeakerLandingPage"),
);
const CookiesPage = lazy(() => import("./pages/legal/CookiesPage"));
const LegalNoticePage = lazy(() => import("./pages/legal/LegalNoticePage"));
const PrivacyPage = lazy(() => import("./pages/legal/PrivacyPage"));
const TermsPage = lazy(() => import("./pages/legal/TermsPage"));
const MeetPage = lazy(() => import("./pages/meet/MeetPage"));
const MeetListPage = lazy(() => import("./pages/meetlist/MeetListPage"));
const PricingPage = lazy(() => import("./pages/pricing/PricingPage"));
const Widgets = lazy(() => import("./pages/widget/Widgets"));
const DisplaysPage = lazy(() => import("./pages/display/DisplaysPage"));
const LicensedPortalPage = lazy(
  () => import("./pages/licensed/LicensedPortalPage"),
);
const LicensedDashboardPage = lazy(
  () => import("./pages/licensed/LicensedDashboardPage"),
);
const MyMeetsListPage = lazy(
  () => import("./pages/licensed/my-meets/MyMeetsListPage.tsx"),
);
const MyMeetPage = lazy(
  () => import("./pages/licensed/my-meets/MyMeetPage.tsx"),
);
const BackupsListPage = lazy(
  () => import("./pages/licensed/backups/BackupsListPage.tsx"),
);
const BackupMeetListPage = lazy(
  () => import("./pages/licensed/backups/BackupMeetsListPage.tsx"),
);
const BackupPage = lazy(
  () => import("./pages/licensed/backups/BackupPage.tsx"),
);
const PlanningBuilderPage = lazy(() =>
  import("./pages/licensed/planning/PlanningBuilderPage").then((m) => ({
    default: m.default,
  })),
);
const PlanningCreatedPage = lazy(
  () => import("./pages/licensed/planning/PlanningCreatedPage"),
);
const MyRefereesPage = lazy(
  () => import("./pages/licensed/referees/MyRefereesPage"),
);
const MyRecordsPage = lazy(
  () => import("./pages/licensed/records/MyRecordsPage"),
);
const MyLevelsPage = lazy(() => import("./pages/licensed/levels/MyLevelsPage"));
const RefereeAvailabilityPage = lazy(
  () => import("./pages/public/RefereeAvailabilityPage"),
);
const CompetitionWorkspace = lazy(
  () => import("./pages/licensed/CompetitionWorkspace"),
);
const LicensedWorkspaceHost = lazy(
  () => import("./pages/licensed/layout-editor/EditorWorkspace"),
);
const LayoutsListPage = lazy(
  () => import("./pages/licensed/layouts/LayoutsListPage"),
);
const ThemesPage = lazy(() => import("./pages/licensed/themes/ThemesPage"));
const ControlRoomPage = lazy(
  () => import("./pages/licensed/control-room/ControlRoomPage"),
);
const LiveMeetHubPage = lazy(
  () => import("./pages/licensed/live/LiveMeetHubPage"),
);
const AdminUsersTab = lazy(
  () => import("./pages/admin/tabs/users/AdminUsersTab"),
);
const AdminLicensesTab = lazy(
  () => import("./pages/admin/tabs/licenses/AdminLicensesTab"),
);
const AdminLicenseTermsTab = lazy(
  () => import("./pages/admin/tabs/license-terms/AdminLicenseTermsTab"),
);
const AdminMeetsTab = lazy(
  () => import("./pages/admin/tabs/meets/AdminMeetsTab"),
);
const AdminOrganisationsTab = lazy(
  () => import("./pages/admin/tabs/organisations/AdminOrganisationsTab"),
);
const AdminRefereesTab = lazy(
  () => import("./pages/admin/tabs/referees/AdminRefereesTab"),
);
const AdminThemesTab = lazy(
  () => import("./pages/admin/tabs/themes/AdminThemesTab"),
);
const AdminLevelsTab = lazy(
  () => import("./pages/admin/tabs/levels/AdminLevelsTab"),
);
const AdminCustomDisciplinesTab = lazy(
  () =>
    import("./pages/admin/tabs/custom-disciplines/AdminCustomDisciplinesTab"),
);
const AdminRecordsTab = lazy(
  () => import("./pages/admin/tabs/records/AdminRecordsTab"),
);
const AdminTestExportTab = lazy(
  () => import("./pages/admin/tabs/test-export/AdminTestExportTab"),
);
const AdminFfForceExportTab = lazy(
  () => import("./pages/admin/tabs/ffforce-export/AdminFfForceExportTab"),
);

/** Hover fill used by CTA “Demander une démo” - shared across contained primary buttons */
const PRIMARY_HOVER = "#1e4492";
const theme = createTheme({
  palette: {
    primary: {
      main: "#2759B6",
      dark: PRIMARY_HOVER,
    },
    text: {
      primary: "#1A1A1A",
      secondary: "#666666",
    },
    background: {
      default: "#FFFFFF",
      paper: "#FFFFFF",
    },
  },
  typography: {
    h1: {
      color: "#1A1A1A",
    },
    h2: {
      color: "#1A1A1A",
    },
    h3: {
      color: "#1A1A1A",
    },
    h4: {
      color: "#1A1A1A",
    },
    h5: {
      color: "#1A1A1A",
    },
    h6: {
      color: "#1A1A1A",
    },
    body1: {
      color: "#333333",
    },
    body2: {
      color: "#666666",
    },
  },
  // Same hover as the homepage demo CTA for every contained primary button
  components: {
    MuiButton: {
      styleOverrides: {
        containedPrimary: {
          "&:hover": {
            backgroundColor: PRIMARY_HOVER,
          },
        },
        outlinedPrimary: {
          "&:hover": {
            borderColor: PRIMARY_HOVER,
            backgroundColor: "rgba(39, 89, 182, 0.04)",
          },
        },
      },
    },
  },
});

function App() {
  return (
    <ThemeProvider theme={theme}>
      <LanguageProvider>
        <SnackProvider>
          <ConfirmDialogProvider>
            <BrowserRouter>
              <AnalyticsProvider>
                <LicenseTermsAcceptanceProvider>
                  <Suspense fallback={<RouteLoadingFallback />}>
                    <Routes>
                      <Route
                        path="/meet/:meetId/athlete-entry"
                        element={<AthleteEntryPage />}
                      />
                      <Route
                        path="/app/:id/widget/custom/:customWidgetId"
                        element={<WidgetView />}
                      />
                      <Route
                        path="/app/:id/widget/:widgetId"
                        element={<WidgetView />}
                      />
                      <Route
                        path="/app/:id/display/custom/:customDisplayId"
                        element={<LayoutDisplayPage />}
                      />
                      <Route
                        path="/app/:id/display/:displayTargetKey"
                        element={<LayoutDisplayPage />}
                      />
                      <Route
                        path="/licensed/layout-preview/:layoutId"
                        element={<LayoutDisplayPreviewPage />}
                      />
                      {/* Routes avec layout minimal */}
                      <Route
                        element={
                          <MinimalLayout>
                            <Outlet />
                          </MinimalLayout>
                        }
                      >
                        <Route path="/app" element={<MeetListPage />} />
                        <Route path="/app/:id" element={<MeetPage />} />
                        <Route
                          path="/app/:id/live"
                          element={<StreamViewPage />}
                        />
                        <Route
                          path="/app/:id/speaker"
                          element={<SpeakerViewPage />}
                        />
                        <Route path="/app/:id/widget" element={<Widgets />} />
                        <Route
                          path="/app/:id/display"
                          element={<DisplaysPage />}
                        />
                      </Route>

                      <Route path="/" element={<HomePage />} />

                      {/* Routes avec layout principal */}
                      <Route
                        element={
                          <MainLayout>
                            <Outlet />
                          </MainLayout>
                        }
                      >
                        {/* Speaker landing (JWT + Speaker/Licensed) */}
                        <Route
                          path="/speaker"
                          element={<SpeakerLandingPage />}
                        />

                        {/* Page de contact */}
                        <Route path="/contact" element={<ContactPage />} />
                        <Route path="/pricing" element={<PricingPage />} />
                        <Route element={<LicensedTermsRouteGuard />}>
                          <Route
                            path="/licensed"
                            element={<LicensedWorkspaceHost />}
                          >
                            <Route element={<LicensedPortalPage />}>
                              <Route
                                index
                                element={<LicensedDashboardPage />}
                              />
                              <Route
                                path="profile"
                                element={
                                  <LicensedDashboardPage section="profile" />
                                }
                              />
                              <Route
                                path="license"
                                element={
                                  <LicensedDashboardPage section="license" />
                                }
                              />
                              <Route
                                path="installer"
                                element={
                                  <LicensedDashboardPage section="installer" />
                                }
                              />
                              <Route
                                path="accepted-terms"
                                element={
                                  <LicensedDashboardPage section="terms" />
                                }
                              />
                              <Route
                                path="my-meets"
                                element={<MyMeetsListPage />}
                              />
                              <Route
                                path="my-meets/:id"
                                element={<MyMeetPage />}
                              />
                              <Route
                                path="backups"
                                element={<BackupsListPage />}
                              />
                              <Route
                                path="backups/meet/:meetId"
                                element={<BackupMeetListPage />}
                              />
                              <Route
                                path="backups/:id"
                                element={<BackupPage />}
                              />
                              <Route
                                path="my-referees"
                                element={<MyRefereesPage />}
                              />
                              <Route
                                path="my-records"
                                element={<MyRecordsPage />}
                              />
                              <Route
                                path="my-levels"
                                element={<MyLevelsPage />}
                              />
                              <Route
                                path="layouts"
                                element={<LayoutsListPage />}
                              />
                              <Route path="themes" element={<ThemesPage />} />
                              <Route
                                path="control-room"
                                element={<ControlRoomPage />}
                              />
                              <Route
                                path="live/:meetId"
                                element={<LiveMeetHubPage />}
                              />
                            </Route>
                            <Route element={<CompetitionWorkspace />}>
                              <Route
                                path="planning/:id"
                                element={<PlanningBuilderPage />}
                              />
                              <Route
                                path="planning/:id/created"
                                element={<PlanningCreatedPage />}
                              />
                            </Route>
                            <Route path="layout-editor" element={null} />
                            <Route
                              path="layout-editor/:layoutId"
                              element={null}
                            />
                          </Route>
                        </Route>
                        <Route
                          path="/referee-availability/:token"
                          element={<RefereeAvailabilityPage />}
                        />

                        {/* Routes d'administration (menu latéral) */}
                        <Route path="/admin" element={<AdminGlobalPage />}>
                          <Route index element={<AdminUsersTab />} />
                          <Route
                            path="licenses"
                            element={<AdminLicensesTab />}
                          />
                          <Route
                            path="license-terms"
                            element={<AdminLicenseTermsTab />}
                          />
                          <Route path="meets" element={<AdminMeetsTab />} />
                          <Route
                            path="organisations"
                            element={<AdminOrganisationsTab />}
                          />
                          <Route
                            path="referees"
                            element={<AdminRefereesTab />}
                          />
                          <Route path="themes" element={<AdminThemesTab />} />
                          <Route path="levels" element={<AdminLevelsTab />} />
                          <Route path="records" element={<AdminRecordsTab />} />
                          <Route
                            path="custom-disciplines"
                            element={<AdminCustomDisciplinesTab />}
                          />
                          <Route path="analytics" element={<AnalyticsPage />} />
                          <Route
                            path="test-export"
                            element={<AdminTestExportTab />}
                          />
                          <Route
                            path="ffforce-export"
                            element={<AdminFfForceExportTab />}
                          />
                        </Route>
                        <Route path="/sign-in" element={<SignIn />} />

                        {/* Routes légales */}
                        <Route path="/terms" element={<TermsPage />} />
                        <Route path="/privacy" element={<PrivacyPage />} />
                        <Route path="/legal" element={<LegalNoticePage />} />
                        <Route path="/cookies" element={<CookiesPage />} />
                      </Route>
                    </Routes>
                  </Suspense>
                  <AnalyticsBanner />
                </LicenseTermsAcceptanceProvider>
              </AnalyticsProvider>
            </BrowserRouter>
          </ConfirmDialogProvider>
        </SnackProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default function AppWithInterfaceDesign() {
  return (
    <InterfaceDesignProvider>
      <App />
    </InterfaceDesignProvider>
  );
}
